import { Models } from "@/api/types";

import {
  ApiProvider,
  CatalogItem,
  CatalogListingItem,
  CatalogMembershipItem,
  MembershipDetailsData,
  OrderDetailsData,
  OrderHistoryData,
  PackageDetailsData,
  ShopData,
} from "./CatalogTypes";
import { FetchCache } from "./FetchCache";
import {
  computeListingBestPriceCents,
  computeMembershipBestPriceCents,
} from "./PricingHelper";

type ListingFilters = {
  types?: string[];
  concerns?: string[];
  areas?: string[];
};

export class CatalogService {
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;
  private listingsCache: FetchCache<Models<"ListingModel">[]>;
  private membershipsCache: FetchCache<Models<"MembershipModel">[]>;
  private discountsCache: FetchCache<Models<"ShopDiscountModel">>;
  private promotionsCache: FetchCache<Models<"PromotionModel">[]>;
  private shopDataCache: FetchCache<ShopData>;

  constructor(private readonly api: ApiProvider) {
    this.listingsCache = new FetchCache<Models<"ListingModel">[]>(
      () => this.api.getListing().then((r) => r ?? []),
      { ttlMs: CatalogService.CACHE_TTL_MS }
    );

    this.membershipsCache = new FetchCache<Models<"MembershipModel">[]>(
      () =>
        (this.api.getMemberships
          ? this.api.getMemberships()
          : Promise.resolve([])
        ).then((r) => r ?? []),
      { ttlMs: CatalogService.CACHE_TTL_MS }
    );

    this.discountsCache = new FetchCache<Models<"ShopDiscountModel">>(
      () =>
        (this.api.getShopDiscounts
          ? this.api.getShopDiscounts()
          : Promise.resolve({
              listingDiscounts: {},
              membershipDiscounts: {},
            } as Models<"ShopDiscountModel">)
        ).then(
          (r) =>
            r ??
            ({
              listingDiscounts: {},
              membershipDiscounts: {},
            } as Models<"ShopDiscountModel">)
        ),
      { ttlMs: CatalogService.CACHE_TTL_MS }
    );

    this.promotionsCache = new FetchCache<Models<"PromotionModel">[]>(
      () =>
        (this.api.getPromotions
          ? this.api.getPromotions()
          : Promise.resolve([])
        ).then((r) => r ?? []),
      { ttlMs: CatalogService.CACHE_TTL_MS }
    );

    this.shopDataCache = new FetchCache<ShopData>(
      () =>
        (this.api.getShopData
          ? this.api.getShopData()
          : Promise.resolve({ concerns: [], areas: [] })
        ).then((r) => r ?? { concerns: [], areas: [] }),
      { ttlMs: CatalogService.CACHE_TTL_MS }
    );
  }

  async getListing(
    filters?: ListingFilters
  ): Promise<Models<"ListingModel">[]> {
    const all = await this.listingsCache.get();
    return this.applyFilters(all, filters);
  }

  async getMemberships(): Promise<Models<"MembershipModel">[]> {
    return this.membershipsCache.get();
  }

  async getDiscounts(): Promise<Models<"ShopDiscountModel">> {
    return this.discountsCache.get();
  }

  async getPromotions(): Promise<Models<"PromotionModel">[]> {
    return this.promotionsCache.get();
  }

  async getShopData(): Promise<ShopData> {
    return this.shopDataCache.get();
  }

  private applyFilters(
    listings: Models<"ListingModel">[],
    filters?: ListingFilters
  ): Models<"ListingModel">[] {
    if (!filters) return listings;

    let result = listings;

    if (filters.types && filters.types.length > 0) {
      const typeSet = new Set(filters.types);
      result = result.filter((l) => typeSet.has(l.type));
    }

    if (filters.concerns && filters.concerns.length > 0) {
      const concernSet = new Set(filters.concerns);
      result = result.filter((l) =>
        (l.concernIds ?? []).some((id) => concernSet.has(id))
      );
    }

    if (filters.areas && filters.areas.length > 0) {
      const areaSet = new Set(filters.areas);
      result = result.filter((l) =>
        (l.areaIds ?? []).some((id) => areaSet.has(id))
      );
    }

    return result;
  }

  async getCatalogListingItems(
    filters?: ListingFilters
  ): Promise<CatalogListingItem[]> {
    const [listings, discounts] = await Promise.all([
      this.getListing(filters),
      this.getDiscounts(),
    ]);
    return listings.map((l) => this.mapListingToCatalog(l, discounts));
  }

  async getCatalogMembershipItems(): Promise<CatalogMembershipItem[]> {
    const [memberships, discounts] = await Promise.all([
      this.getMemberships(),
      this.getDiscounts(),
    ]);
    return memberships.map((m) => this.mapMembershipToCatalog(m, discounts));
  }

  async getCatalogItems(filters?: ListingFilters): Promise<CatalogItem[]> {
    const [listingItems, membershipItems] = await Promise.all([
      this.getCatalogListingItems(filters),
      this.getCatalogMembershipItems(),
    ]);
    return [...listingItems, ...membershipItems];
  }

  /**
   * Lightweight index of listing names and types for building custom instructions
   * or summarizing available treatments/packages without loading detailed pricing.
   */
  async getListingOverview(): Promise<
    Array<{
      id: string;
      title: string;
      type: "Treatment" | "Package";
      description: string | null;
    }>
  > {
    const listings = await this.getListing();
    return listings.map((listing) => ({
      id: listing.id,
      title: listing.title,
      type: (listing.type as unknown as "Treatment" | "Package") ?? "Package",
      description: listing.description ?? null,
    }));
  }

  async getCatalogListingItemById(
    id: string
  ): Promise<CatalogListingItem | null> {
    const [listings, discounts] = await Promise.all([
      this.listingsCache.get(),
      this.getDiscounts(),
    ]);

    // remove optional `/pdp/package/` string start part from id
    const idWithoutPdp = id.replace(/^\/pdp\/package\//, "");
    const listing = listings.find((l) => l.id === idWithoutPdp);
    if (!listing) return null;

    const baseItem = this.mapListingToCatalog(listing, discounts);

    if (this.api.getListingShopDiscounts) {
      try {
        const detailed = await this.api.getListingShopDiscounts(idWithoutPdp);
        const rawPackageDiscounts = detailed?.packageDiscounts ?? {};

        // Filter out undefined values to match the expected type
        const packageDiscounts: Record<
          string,
          Models<"ShopProductDiscountModel">
        > = {};
        for (const [key, value] of Object.entries(rawPackageDiscounts)) {
          if (value) {
            packageDiscounts[key] = value;
          }
        }

        const allDiscounts = Object.values(packageDiscounts);

        return {
          ...baseItem,
          packageDiscounts,
          availableDiscounts: allDiscounts,
        };
      } catch {
        return baseItem;
      }
    }

    return baseItem;
  }

  async getCatalogMembershipItemById(
    id: string
  ): Promise<CatalogMembershipItem | null> {
    const [memberships, discounts] = await Promise.all([
      this.getMemberships(),
      this.getDiscounts(),
    ]);
    const membership = memberships.find((m) => m.id === id);
    if (!membership) return null;
    const baseItem = this.mapMembershipToCatalog(membership, discounts);
    const d = discounts.membershipDiscounts?.[id] ?? null;
    return {
      ...baseItem,
      availableDiscounts: d ? [d] : [],
    };
  }

  async getCatalogListingItemsByIds(
    ids: string[]
  ): Promise<CatalogListingItem[]> {
    if (!Array.isArray(ids) || ids.length === 0) return [];

    const normalizedIds = ids
      .map((id) =>
        typeof id === "string" ? id.replace(/^\/pdp\/package\//, "") : ""
      )
      .filter((x) => !!x);

    if (normalizedIds.length === 0) return [];

    const [listings, discounts] = await Promise.all([
      this.listingsCache.get(),
      this.getDiscounts(),
    ]);

    const idSet = new Set(normalizedIds);
    const found = listings.filter((l) => idSet.has(l.id));
    const mapped = found.map((l) => this.mapListingToCatalog(l, discounts));

    const byId = new Map(mapped.map((m) => [m.id, m] as const));
    const ordered: CatalogListingItem[] = [];
    const seen = new Set<string>();
    for (const id of normalizedIds) {
      if (seen.has(id)) continue;
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        seen.add(id);
      }
    }
    return ordered;
  }

  async getCatalogMembershipItemsByIds(
    ids: string[]
  ): Promise<CatalogMembershipItem[]> {
    if (!Array.isArray(ids) || ids.length === 0) return [];

    const normalizedIds = ids.filter(
      (x): x is string => typeof x === "string" && x.length > 0
    );
    if (normalizedIds.length === 0) return [];

    const [memberships, discounts] = await Promise.all([
      this.getMemberships(),
      this.getDiscounts(),
    ]);

    const idSet = new Set(normalizedIds);
    const found = memberships.filter((m) => idSet.has(m.id));
    const mapped = found.map((m) => this.mapMembershipToCatalog(m, discounts));

    const byId = new Map(mapped.map((m) => [m.id, m] as const));
    const ordered: CatalogMembershipItem[] = [];
    const seen = new Set<string>();
    for (const id of normalizedIds) {
      if (seen.has(id)) continue;
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        seen.add(id);
      }
    }
    return ordered;
  }

  // Package details fetching
  fetchEssentialPackageDetails = async (
    id: string
  ): Promise<PackageDetailsData> => {
    const idWithoutPdp = id.replace(/^\/pdp\/package\//, "");

    try {
      const [
        listing,
        packages,
        expectations,
        shopDiscounts,
        shopDiscountsListingRaw,
      ] = await Promise.all([
        this.api.getListingById
          ? this.api.getListingById(idWithoutPdp)
          : Promise.reject(new Error("getListingById not available")),
        this.api.getListingPackages
          ? this.api.getListingPackages(idWithoutPdp)
          : Promise.resolve([]),
        this.api.getListingExpectations
          ? this.api.getListingExpectations(idWithoutPdp)
          : Promise.resolve([]),
        this.api.getShopDiscounts
          ? this.api.getShopDiscounts()
          : Promise.resolve({
              listingDiscounts: {},
              membershipDiscounts: {},
            } as Models<"ShopDiscountModel">),
        this.api.getListingShopDiscounts
          ? this.api.getListingShopDiscounts(idWithoutPdp)
          : Promise.resolve({
              packageDiscounts: {},
            } as { packageDiscounts?: Record<string, Models<"ShopProductDiscountModel">> }),
      ]);

      const shopDiscountsListing: {
        packageDiscounts?: Record<string, Models<"ShopProductDiscountModel">>;
      } = {
        packageDiscounts:
          (
            shopDiscountsListingRaw as {
              packageDiscounts?: Record<
                string,
                Models<"ShopProductDiscountModel">
              >;
            }
          )?.packageDiscounts ?? {},
      };

      return {
        listing,
        packages,
        expectations,
        shopDiscounts,
        shopDiscountsListing,
      };
    } catch (error) {
      throw new Error(
        `Failed to fetch package details: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  // Membership details fetching
  fetchEssentialMembershipDetails = async (
    id: string
  ): Promise<MembershipDetailsData> => {
    try {
      const [membership, membershipRewards, shopDiscounts] = await Promise.all([
        this.api.getMembershipDetails
          ? this.api.getMembershipDetails(id)
          : Promise.reject(new Error("getMembershipDetails not available")),
        this.api.getMembershipRewards
          ? this.api.getMembershipRewards(id)
          : Promise.resolve([]),
        this.getDiscounts(),
      ]);

      return {
        membership,
        membershipRewards,
        shopDiscounts,
      };
    } catch (error) {
      throw new Error(
        `Failed to fetch membership details: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  // List all available discounts for the user
  async executeListAvailableDiscounts(): Promise<{
    shopDiscounts: Models<"ShopDiscountModel">;
    promotions: Models<"PromotionModel">[];
    listings: Models<"ListingModel">[];
    memberships: Models<"MembershipModel">[];
  }> {
    const [shopDiscounts, promotions, listings, memberships] =
      await Promise.all([
        this.getDiscounts(),
        this.getPromotions(),
        this.getListing(),
        this.getMemberships(),
      ]);

    return {
      shopDiscounts,
      promotions,
      listings,
      memberships,
    };
  }

  // Order history execution method
  async executeGetOrderHistory(args: {
    detailed?: boolean;
    orderIds?: string[];
    forContext?: boolean;
  }): Promise<{
    type: "single" | "multiple" | "summary" | "detailed" | "behavior";
    data: OrderDetailsData | OrderDetailsData[] | OrderHistoryData;
    details?: OrderDetailsData[];
  }> {
    // Handle specific order ID(s) request
    if (args.orderIds && args.orderIds.length > 0) {
      if (args.orderIds.length === 1) {
        const orderDetails = await this.fetchOrderDetailsWithLineItems(
          args.orderIds[0]
        );
        return { type: "single", data: orderDetails };
      } else {
        const orderDetails = await this.fetchMultipleOrderDetailsWithLineItems(
          args.orderIds
        );
        return { type: "multiple", data: orderDetails };
      }
    }

    // Get order history
    if (!this.api.getOrderHistory) {
      throw new Error("Order history is not available.");
    }

    const orderHistory = await this.api.getOrderHistory(1);

    if (!orderHistory || orderHistory.orders.length === 0) {
      throw new Error("No order history found.");
    }

    // For AI context analysis
    if (args.forContext) {
      const detailPromises = orderHistory.orders
        .slice(0, 10)
        .map((order) =>
          this.fetchOrderDetailsWithLineItems(order.id).catch(() => null)
        );
      const details = (await Promise.all(detailPromises)).filter(
        (d): d is OrderDetailsData => d !== null
      );

      return { type: "behavior", data: orderHistory, details };
    }

    // For user display with optional details
    if (args.detailed) {
      const detailPromises = orderHistory.orders
        .slice(0, 3)
        .map((order) =>
          this.fetchOrderDetailsWithLineItems(order.id).catch(() => null)
        );
      const details = (await Promise.all(detailPromises)).filter(
        (d): d is OrderDetailsData => d !== null
      );

      return { type: "detailed", data: orderHistory, details };
    }

    return { type: "summary", data: orderHistory };
  }

  // Helper to fetch multiple order details
  private async fetchMultipleOrderDetailsWithLineItems(
    orderIds: string[]
  ): Promise<OrderDetailsData[]> {
    const orderPromises = orderIds.map((orderId) =>
      this.fetchOrderDetailsWithLineItems(orderId).catch(() => null)
    );
    const results = await Promise.all(orderPromises);
    return results.filter((order): order is OrderDetailsData => order !== null);
  }

  // Helper to fetch complete order details
  private async fetchOrderDetailsWithLineItems(
    orderId: string
  ): Promise<OrderDetailsData> {
    if (!this.api.getOrderDetails) {
      throw new Error("Order details API not available");
    }

    const order = await this.api.getOrderDetails(orderId);

    // Fetch package and membership details
    const packagePromises = order.packageLineItems.map((item) => {
      if (this.api.getPackageById) {
        return this.api.getPackageById(item.packageId).catch(() => null);
      }
      return null;
    });

    const membershipPromises = order.membershipLineItems.map((item) => {
      if (this.api.getMembershipDetails) {
        return this.api
          .getMembershipDetails(item.membershipId)
          .catch(() => null);
      }
      return null;
    });

    const [packages, memberships] = await Promise.all([
      Promise.all(packagePromises),
      Promise.all(membershipPromises),
    ]);

    return {
      order,
      packages: packages.filter((p): p is Models<"PackageModel"> => p !== null),
      memberships: memberships.filter(
        (m): m is Models<"MembershipModel"> => m !== null
      ),
    };
  }

  // Private methods
  private mapListingToCatalog(
    listingItem: Models<"ListingModel">,
    discounts: Models<"ShopDiscountModel">
  ): CatalogListingItem {
    const listingDiscount =
      discounts.listingDiscounts?.[listingItem.id] ?? null;
    const pricingDetails = computeListingBestPriceCents({
      basePriceCents: listingItem.baseCost ?? 0,
      membershipPriceCents: listingItem.membershipCost ?? null,
      discount: listingDiscount ?? undefined,
    });

    return {
      id: listingItem.id,
      type: listingItem.type as "Treatment" | "Package",
      title: listingItem.title,
      description: listingItem.description ?? null,
      imageUrl: listingItem.imageUrl ?? null,
      basePriceCents: listingItem.baseCost ?? 0,
      pricingDetails: pricingDetails,
      concernIds: listingItem.concernIds ?? [],
      areaIds: listingItem.areaIds ?? [],
      unitType: listingItem.packageUnitType ?? null,
    };
  }

  private mapMembershipToCatalog(
    m: Models<"MembershipModel">,
    discounts: Models<"ShopDiscountModel">
  ): CatalogMembershipItem {
    const membershipDiscount = discounts.membershipDiscounts?.[m.id] ?? null;
    const r = computeMembershipBestPriceCents({
      basePriceCents: m.monthlyCost ?? m.billingPeriodCost ?? 0,
      discount: membershipDiscount ?? undefined,
    });
    return {
      id: m.id,
      type: "Membership",
      title: m.name,
      description: m.description ?? null,
      imageUrl: m.imageUrl ?? null,
      basePriceCents: m.monthlyCost ?? m.billingPeriodCost ?? 0,
      pricingDetails: r,
      billingPeriodInterval: m.billingPeriodInterval,
      billingPeriodIntervalCount: m.billingPeriodIntervalCount,
    };
  }
}
